import { useFlags } from "launchdarkly-react-client-sdk";
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo
} from "react";
import { Control, useWatch } from "react-hook-form";

import {
  EmailBrandingFormFrame,
  EmailBrandingFormNotice
} from "components/email-branding";
import {
  EmailEditorTab,
  EmailEditorTabs,
  useEmailEditorTabs
} from "components/email-builder";
import { EXTENDED_EMAIL_BUILDER_SPECIAL_LINKS } from "components/email-builder/email-builder-merge-tags";
import { useEmailBuilderMergeTags } from "components/email-builder/use-email-builder-merge-tags.hook";
import InputFormField from "components/input-form-field";
import WordProcessorField from "components/word-processor-field";
import {
  DRAG_AND_DROP_EMAIL_BUILDER,
  TOUCHPOINTS_WORDPROCESSOR_FIELD
} from "constants/feature-flags";
import {
  COMMUNICATION_METHOD_EMAIL,
  COMMUNICATION_METHOD_TEXT_SMS
} from "constants/patients";
import {
  EMAIL_TEMPLATE_DESIGN_ONLY_TYPES,
  EMAIL_TEMPLATE_WITH_DESIGN_TYPES,
  MESSAGE_TEMPLATE_TYPES,
  TEMPLATE_TYPE_DESIGNER_EMAIL
} from "constants/templates";
import { type TextTemplate } from "services/types/text-templates.type";
import { getTextTemplateSubjectValue } from "utils/message-templates.util";

import MessageLabelWithWarning from "./message-label-with-warning.component";
import { NewTouchpointFormValues } from "./new-touchpoint-form";

type TouchpointMessageFieldProps = {
  height?: number;
  control: Control<NewTouchpointFormValues>;
  communicationMethod: string | number | null;
  onMessageUpdate?: (html: string) => void;
  onSubjectUpdate?: (subject: string) => void;
};

export type TouchpointMessageFieldRef = {
  exportHtml: () => Promise<{ html: string; design: UnlayerDesign } | null>;
  isEmailBuilder: boolean;
};

const TouchpointMessageField = forwardRef<
  TouchpointMessageFieldRef,
  TouchpointMessageFieldProps
>(
  (
    { height, control, communicationMethod, onMessageUpdate, onSubjectUpdate },
    ref
  ) => {
    const flags = useFlags();
    const isTouchpointsWordProcessorField =
      flags[TOUCHPOINTS_WORDPROCESSOR_FIELD];
    const isDragAndDropEmailEnabled = flags[DRAG_AND_DROP_EMAIL_BUILDER];
    const patient = useWatch({ control, name: "patient" });

    const isTextOrSms =
      Number(communicationMethod) === COMMUNICATION_METHOD_TEXT_SMS;
    const isEmail = Number(communicationMethod) === COMMUNICATION_METHOD_EMAIL;

    const templateTypes = useMemo(() => {
      return isTextOrSms
        ? MESSAGE_TEMPLATE_TYPES
        : EMAIL_TEMPLATE_WITH_DESIGN_TYPES;
    }, [isTextOrSms]);

    // Email builder tabs state
    const {
      emailBuilderRef,
      activeTab,
      setActiveTab,
      isEmailBuilder,
      exportHtml: exportHtmlFromBuilder,
      loadDesign,
      setActiveDesign,
      activeDesign,
      editorTabIsDisabled,
      setEditorTabIsDisabled
    } = useEmailEditorTabs();

    const emailBuilderMergeTags = useEmailBuilderMergeTags();

    // Expose exportHtml method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        exportHtml: async () => {
          if (!isEmailBuilder || !isDragAndDropEmailEnabled) {
            return null;
          }
          try {
            return await exportHtmlFromBuilder();
          } catch (error) {
            console.error("Failed to export email builder HTML:", error);
            return null;
          }
        },
        isEmailBuilder: isEmailBuilder && isDragAndDropEmailEnabled
      }),
      [isEmailBuilder, exportHtmlFromBuilder, isDragAndDropEmailEnabled]
    );

    const handleTemplateInserted = useCallback(
      (template: TextTemplate | null) => {
        const subject = getTextTemplateSubjectValue(template?.subject_template);
        if (subject) {
          onSubjectUpdate?.(subject);
        }

        if (
          template?.type === TEMPLATE_TYPE_DESIGNER_EMAIL &&
          template?.email_design_json
        ) {
          loadDesign(template.email_design_json);
        }
      },
      [loadDesign, onSubjectUpdate]
    );

    if (isTouchpointsWordProcessorField && (isTextOrSms || isEmail)) {
      const wordProcessorField = (
        <WordProcessorField
          name="message"
          control={control}
          placeholder=""
          featuresGroup={isTextOrSms ? "textOnly" : "light"}
          allowTemplates={isTextOrSms || (isEmail && isDragAndDropEmailEnabled)}
          allowMergeTags
          allowInsertMergeTagsValues
          allowToolbarAutohide={false}
          allowImageAutoUpload={!isTextOrSms}
          smsSegmentCounter={isTextOrSms}
          // The picker inserts resolved values, but the touchpoint is sent later
          // by `make_message`, which runs `format_template` over the stored text
          // — so a literal tag (typed by hand, or from a template inserted before
          // a patient was picked) still expands at send time.
          estimateSmsMergeTags
          templateTypes={templateTypes}
          autoFocus
          required={
            activeTab !== EmailEditorTab.EmailBuilder &&
            (isTextOrSms || (isEmail && !isEmailBuilder))
          }
          mergeTagsPatientId={patient?.id}
          height={height}
          allowImages={isEmail}
          maxImageSizeMB={2}
          onTemplateInserted={isEmail ? handleTemplateInserted : undefined}
        />
      );

      // A touchpoint email goes out as the staff member who creates it, from the patient's clinic.
      const withBrandingFrame = (content: ReactNode) => (
        <EmailBrandingFormFrame
          control={control}
          bodyName="message"
          kind="marketing"
          isDesigner={isDragAndDropEmailEnabled && isEmailBuilder}
          clinicId={patient?.preferred_clinic?.id ?? undefined}
          sender="current-user"
          example
        >
          {content}
        </EmailBrandingFormFrame>
      );

      return (
        <>
          <MessageLabelWithWarning />
          {isEmail && (
            <EmailBrandingFormNotice
              control={control}
              bodyName="message"
              kind="marketing"
              isDesigner={isDragAndDropEmailEnabled && isEmailBuilder}
            />
          )}
          {isEmail && isDragAndDropEmailEnabled ? (
            <EmailEditorTabs
              emailBuilderRef={emailBuilderRef}
              initialDesign={activeDesign}
              setActiveDesign={setActiveDesign}
              showTabs={true}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              editorTabIsDisabled={editorTabIsDisabled}
              setEditorTabIsDisabled={setEditorTabIsDisabled}
              templateTypes={EMAIL_TEMPLATE_DESIGN_ONLY_TYPES}
              onExportFromBuilder={(html) => {
                onMessageUpdate?.(html);
              }}
              editorContent={wordProcessorField}
              mergeTags={emailBuilderMergeTags}
              specialLinks={EXTENDED_EMAIL_BUILDER_SPECIAL_LINKS}
              contentWrapper={withBrandingFrame}
            />
          ) : isEmail ? (
            withBrandingFrame(wordProcessorField)
          ) : (
            wordProcessorField
          )}
        </>
      );
    }

    return (
      <InputFormField
        name="message"
        label={<MessageLabelWithWarning />}
        control={control}
        multiline
        sx={{ ml: 0 }}
        required
      />
    );
  }
);

TouchpointMessageField.displayName = "TouchpointMessageField";

export default TouchpointMessageField;
